/** IXD/DSD 비교 메인 페이지 */

import { useCallback, useMemo, useRef, useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  IxdChunk,
  ChunkSummaryItem,
  logIxdDsdCompareDownload,
} from "@/apis/compare/route";
import { fuzzIxd } from "@/apis/fuzz/route";
import { downloadBlob } from "@/utils/download-blob";
import queries from "@/queries";
import { useIxdDsdCompareStore } from "@/stores/ixd-dsd-compare";
import {
  buildCellIdentityIndex,
  buildTableLabelMap,
  type CellIdentityLocation,
} from "@/utils/ixd-cell-model";
import { EditSessionStoreProvider } from "@/hooks/ixd-edit-session";
import { IxdDsdCompareStoreProvider } from "./compare-store-provider";
import {
  useCompare,
  type IxdSourceMode,
} from "@/hooks/ixd-dsd-compare/use-compare";
import { useCompareUrlState } from "@/hooks/ixd-dsd-compare/use-compare-url-state";
import { useChunkDetail } from "@/hooks/ixd-dsd-compare/use-chunk-detail";
import { useNavigation } from "@/hooks/ixd-dsd-compare/use-navigation";
import { useKeyboardShortcuts } from "@/hooks/ixd-dsd-compare/use-keyboard-shortcuts";
import {
  CompareSidebar,
  type CompareSidebarCategory,
  type CompareSidebarSection,
} from "@/components/compare/compare-sidebar";
import { SidebarInset, SidebarProvider } from "@/styles/ui/sidebar";
import { CompareContentView } from "@/components/ixd-dsd-compare/compare-content-view";
import {
  buildDsdDlRegionViews,
  buildDsdLineItemRegionViews,
  buildDsdMemberRegionViews,
} from "@/components/ixd-dsd-compare/dsd-region-views";
import {
  serializeLinkageIndex,
  type SerializedLinkageIndex,
} from "@/components/ixd-dsd-compare/export/linkage-index";
import { InitialUploadView } from "@/components/ixd-dsd-compare/initial-upload-view";
import { LeftSidebar } from "@/components/app-shell";
import { fetchAssetAsDataUri, useHtmlExport } from "@/lib/html-export";
import {
  FOCUS_FAILED_MESSAGE,
  focusWithRetry,
} from "@/utils/ixd-dsd-compare/focus-with-retry";
import {
  PARAM_IXD_REPORT_ID,
  PARAM_SOURCE_MODE,
} from "@/utils/ixd-dsd-compare/comparison-url";
import * as Sentry from "@sentry/react";

/** 페이지 루트: Provider로 감싸서 페이지 스코프 스토어 제공 */
export const IxdDsdComparePage = () => (
  <IxdDsdCompareStoreProvider>
    <EditSessionStoreProvider>
      <IxdDsdComparePageContent />
    </EditSessionStoreProvider>
  </IxdDsdCompareStoreProvider>
);

/** 실제 페이지 본문 (스토어 컨텍스트 내부) */
const IxdDsdComparePageContent = () => {
  // UI 상태 (스토어 - ID만)
  const dsdFile = useIxdDsdCompareStore((s) => s.dsdFile);
  const ixdFile = useIxdDsdCompareStore((s) => s.ixdFile);
  const setDsdFile = useIxdDsdCompareStore((s) => s.setDsdFile);
  const setIxdFile = useIxdDsdCompareStore((s) => s.setIxdFile);
  // comparisonId / activeChunkId는 URL 쿼리 파라미터에서 관리 (새로고침 복원)
  const {
    comparisonId,
    activeChunkId,
    openComparisonInNewTab,
    setActiveChunkId,
  } = useCompareUrlState();
  const activeIxdChunkId = useIxdDsdCompareStore((s) => s.activeIxdChunkId);
  const setActiveIxdChunkId = useIxdDsdCompareStore(
    (s) => s.setActiveIxdChunkId,
  );
  const setSelectedFsType = useIxdDsdCompareStore((s) => s.setSelectedFsType);
  const selectedFsType = useIxdDsdCompareStore((s) => s.selectedFsType);
  const isSyncScroll = useIxdDsdCompareStore((s) => s.isSyncScroll);
  // 대사 화면 여부는 URL의 comparisonId 존재 여부에서 파생
  const isCompareView = !!comparisonId;
  const matchNavigation = useIxdDsdCompareStore((s) => s.matchNavigation);
  const setMatchNavigation = useIxdDsdCompareStore((s) => s.setMatchNavigation);
  const queryClient = useQueryClient();

  // Server state (React Query)
  const { data: comparisonSummary } = useQuery({
    ...queries.comparison(comparisonId ?? ""),
    enabled: !!comparisonId,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  const {
    data: chunkDetail,
    isLoading: isChunkLoading,
    isFetching: isChunkFetching,
  } = useChunkDetail(comparisonId, activeChunkId);

  // 비교 화면 진입/복귀 시 누락된 chunk detail만 active chunk부터 순서대로 채운다.
  useEffect(() => {
    if (
      !comparisonId ||
      !activeChunkId ||
      comparisonSummary?.comparisonId !== comparisonId ||
      !comparisonSummary.chunks.length
    ) {
      return;
    }

    let isCancelled = false;

    const hydrateMissingChunks = async () => {
      const activeChunk = comparisonSummary.chunks.find(
        (chunk) => chunk.id === activeChunkId,
      );
      const orderedChunks = activeChunk
        ? [
            activeChunk,
            ...comparisonSummary.chunks.filter(
              (chunk) => chunk.id !== activeChunkId,
            ),
          ]
        : comparisonSummary.chunks;

      for (const chunk of orderedChunks) {
        if (isCancelled) return;

        const chunkQuery = queries
          .comparison(comparisonId)
          ._ctx.chunk(chunk.id);
        if (queryClient.getQueryData(chunkQuery.queryKey)) {
          continue;
        }

        await queryClient.prefetchQuery({
          ...chunkQuery,
          staleTime: 10 * 60 * 1000,
          gcTime: 30 * 60 * 1000,
        });
      }
    };

    void hydrateMissingChunks();

    return () => {
      isCancelled = true;
    };
  }, [activeChunkId, comparisonId, comparisonSummary, queryClient]);

  const setIxdReportId = useIxdDsdCompareStore((s) => s.setIxdReportId);

  // Summary 로드 시 비교 메타를 동기화하고, URL의 active chunk 상태를 보정한다.
  // - chunkId가 없거나 더 이상 유효하지 않으면 첫 chunk로 복구 (URL replace)
  // - chunk는 유효하지만 IXD 하위 선택(activeIxdChunkId)이 비어 있으면(새로고침 직후) 채운다
  useEffect(() => {
    if (!comparisonSummary || comparisonSummary.comparisonId !== comparisonId) {
      return;
    }

    setIxdReportId(comparisonSummary.ixdReportId);
    // fsType은 DSD 메타데이터(연결/별도)에서 가져온다. top-level reportBase는
    // "separate"/"consolidated"로 정규화돼 있지 않아 별도가 연결로 폴백되는 버그가 있었다.
    const base =
      comparisonSummary.dsdMetadata?.reportBase ?? comparisonSummary.reportBase;
    setSelectedFsType(base === "separate" ? "separate" : "consolidated");

    const chunks = comparisonSummary.chunks;
    if (chunks.length === 0) return;

    const activeChunk = activeChunkId
      ? chunks.find((c) => c.id === activeChunkId)
      : undefined;

    if (!activeChunk) {
      const firstChunk = chunks[0];
      setActiveChunkId(firstChunk.id);
      setActiveIxdChunkId(firstChunk.mappedIxdChunkIds[0] ?? null);
      return;
    }

    if (!activeIxdChunkId) {
      setActiveIxdChunkId(activeChunk.mappedIxdChunkIds[0] ?? null);
    }
  }, [
    comparisonSummary,
    comparisonId,
    activeChunkId,
    activeIxdChunkId,
    setIxdReportId,
    setActiveChunkId,
    setActiveIxdChunkId,
    setSelectedFsType,
  ]);

  const handleDsdSelect = (file: File) => setDsdFile(file);
  const handleIxdSelect = (file: File) => setIxdFile(file);

  // Refs
  const ixdRendererRef =
    useRef<import("@/components/ixd-preview").IxdPreviewRendererRef>(null);

  // initial-upload 화면 local state — 비교 결과 진입 후엔 store의 ixdReportId만 사용
  // 홈의 "이전 IXD 불러오기"처럼 기존 IXD 탭으로 바로 보내는 진입점만 지원한다(진입 시 1회).
  const [searchParams] = useSearchParams();
  const initialSourceMode: IxdSourceMode =
    searchParams.get(PARAM_SOURCE_MODE) === "existing-report"
      ? "existing-report"
      : "upload";
  const [sourceMode, setSourceMode] =
    useState<IxdSourceMode>(initialSourceMode);
  // 기존 IXD 탭으로 들어올 때만 미리 고른다 — 다른 모드에서는 피커가 없어 보이지 않는 선택이 된다.
  const [selectedIxdReportId, setSelectedIxdReportId] = useState<string | null>(
    () =>
      initialSourceMode === "existing-report"
        ? searchParams.get(PARAM_IXD_REPORT_ID)
        : null,
  );
  const [pendingIdentityLocation, setPendingIdentityLocation] =
    useState<CellIdentityLocation | null>(null);
  const [focusedDsdResetKey, setFocusedDsdResetKey] = useState(0);

  const { isComparing, handleCompare, canCompare } = useCompare({
    dsdFile,
    ixdFile,
    selectedIxdReportId,
    sourceMode,
  });

  /**
   * 모드가 바뀌면 고른 IXD 쪽 파일을 버린다. accept/useAcceptedFileSelect 는
   * "고르는 시점"만 막으므로, 남겨두면 `.ixd` 가 공시본 대사로(또는 그 반대로)
   * 그대로 올라간다 — canCompare 는 파일 존재만 보기 때문에 막히지도 않는다.
   */
  const handleSourceModeChange = (next: IxdSourceMode) => {
    setSourceMode(next);
    setIxdFile(null);
  };

  const fuzzMutation = useMutation({
    mutationFn: () => {
      if (!ixdFile) throw new Error("IXD 파일이 필요합니다.");
      return fuzzIxd(ixdFile, dsdFile ?? undefined);
    },
    onSuccess: ({ blob, filename }) => {
      downloadBlob(blob, filename);
    },
  });

  const handleFuzz = () => fuzzMutation.mutate();

  // 현재 chunk detail에서 파생되는 데이터
  const currentIxdChunks = useMemo<IxdChunk[]>(() => {
    return chunkDetail?.ixdChunks || [];
  }, [chunkDetail]);

  // activeChunk를 summary에서 파생
  const activeChunk = useMemo(() => {
    if (!comparisonSummary || !activeChunkId) return null;
    const chunk = comparisonSummary.chunks.find((c) => c.id === activeChunkId);
    if (!chunk) return null;
    // DSDChunkInfo 호환 객체로 변환 (sidebar/content에서 사용)
    return {
      id: chunk.id,
      title: chunk.title,
      subChunks: chunkDetail?.dsdChunk?.subChunks || [],
    };
  }, [comparisonSummary, activeChunkId, chunkDetail]);

  // DL·기간 영역: 서버가 근거로 배정한 영역. 렌더러가 셀 attribute 로 싣는다.
  const dlRegions = useMemo(
    () =>
      buildDsdDlRegionViews(chunkDetail?.dsdDlRegions, chunkDetail?.ixdChunks),
    [chunkDetail],
  );
  const periodRegions = useMemo(
    () => chunkDetail?.dsdPeriodRegions ?? null,
    [chunkDetail],
  );
  const memberRegions = useMemo(
    () =>
      buildDsdMemberRegionViews(
        chunkDetail?.dsdMemberRegions,
        chunkDetail?.dsdDlRegions,
        chunkDetail?.ixdChunks,
      ),
    [chunkDetail],
  );
  const lineItemRegions = useMemo(
    () =>
      buildDsdLineItemRegionViews(
        chunkDetail?.dsdLineItemRegions,
        chunkDetail?.dsdDlRegions,
        chunkDetail?.ixdChunks,
      ),
    [chunkDetail],
  );

  // 검토 모드 헤더 태그: dsdId → 태그 맵 (헤더 셀 클릭 popover 용)
  const headerTagsByDsdId = useMemo(() => {
    const tags = chunkDetail?.dsdHeaderTags;
    if (!tags?.length) return null;
    return new Map(tags.map((t) => [t.dsdId, t]));
  }, [chunkDetail]);

  // activeIxdChunk를 chunkDetail에서 파생
  const activeIxdChunk = useMemo(() => {
    if (!activeIxdChunkId || !currentIxdChunks.length) return null;
    return currentIxdChunks.find((c) => c.id === activeIxdChunkId) || null;
  }, [activeIxdChunkId, currentIxdChunks]);

  // dsdPreview 호환 객체 (CompareContentView에서 사용)
  const dsdPreview = useMemo(() => {
    if (!comparisonSummary) return null;
    return {
      chunks: comparisonSummary.chunks.map((c) => ({
        id: c.id,
        title: c.title,
        subChunks: [],
      })),
      companyName: comparisonSummary.companyName,
      reportBase: comparisonSummary.reportBase,
    };
  }, [comparisonSummary]);

  const handleActiveIxdChunkChange = useCallback(
    (chunk: IxdChunk) => setActiveIxdChunkId(chunk.id),
    [setActiveIxdChunkId],
  );

  const navigation = useNavigation({
    ixdRendererRef,
    matchNavigation,
    onMatchNavigationChange: setMatchNavigation,
    currentIxdChunks,
    onActiveIxdChunkChange: handleActiveIxdChunkChange,
    activeIxdChunkId: activeIxdChunkId ?? undefined,
  });

  const handleCompareFocusClose = useCallback(() => {
    navigation.handleNavigationClose();
    setFocusedDsdResetKey((key) => key + 1);
  }, [navigation.handleNavigationClose]);

  const handleFocusIdentityLocation = useCallback(
    (location: CellIdentityLocation): boolean => {
      const targetDsdChunk = comparisonSummary?.chunks.find((chunk) =>
        chunk.mappedIxdChunkIds.includes(location.chunkId),
      );

      if (!targetDsdChunk) {
        toast.warning("현재 비교 결과에서 해당 IXD 위치로 이동할 수 없습니다.");
        return false;
      }

      if (targetDsdChunk && targetDsdChunk.id !== activeChunkId) {
        setActiveChunkId(targetDsdChunk.id);
      }
      setActiveIxdChunkId(location.chunkId);
      setMatchNavigation(null);
      setPendingIdentityLocation(location);
      return true;
    },
    [
      activeChunkId,
      comparisonSummary,
      setActiveChunkId,
      setActiveIxdChunkId,
      setMatchNavigation,
    ],
  );

  useEffect(() => {
    if (!pendingIdentityLocation) return;
    const location: CellIdentityLocation = pendingIdentityLocation;
    if (!currentIxdChunks.some((chunk) => chunk.id === location.chunkId)) {
      return;
    }

    return focusWithRetry(
      () => ixdRendererRef.current?.focusCellLocation(location) ?? false,
      {
        onSuccess: () => setPendingIdentityLocation(null),
        onGiveUp: () => {
          toast.warning(FOCUS_FAILED_MESSAGE);
          setPendingIdentityLocation(null);
        },
      },
    );
  }, [currentIxdChunks, pendingIdentityLocation]);

  useKeyboardShortcuts({
    matchNavigation,
    onNavigatePrev: navigation.handleNavigatePrev,
    onNavigateNext: navigation.handleNavigateNext,
    onNavigateClose: handleCompareFocusClose,
  });

  const handleChunkClick = useCallback(
    (chunk: ChunkSummaryItem) => {
      setActiveChunkId(chunk.id);

      // 매핑된 첫 번째 IXD chunk 자동 선택 (없으면 null로 초기화)
      setActiveIxdChunkId(chunk.mappedIxdChunkIds[0] ?? null);

      // DSD 프리뷰 첫 섹션으로 스크롤
      const sectionEl = document.getElementById(`${chunk.id}-1`);
      if (sectionEl) {
        sectionEl.scrollIntoView({ behavior: "auto", block: "start" });
      }

      // 매칭 네비게이션 및 DSD focus 초기화
      setMatchNavigation(null);
      setFocusedDsdResetKey((key) => key + 1);
    },
    [setActiveChunkId, setActiveIxdChunkId, setMatchNavigation],
  );

  // 사이드바: chunks를 본문/주석 카테고리로 변환 + id→chunk 역조회 맵
  const { sidebarCategories, chunkById } = useMemo(() => {
    const chunks = comparisonSummary?.chunks ?? [];
    const byId = new Map<string, ChunkSummaryItem>();
    const seenTitles = new Set<string>();
    const body: CompareSidebarSection[] = [];
    const note: CompareSidebarSection[] = [];

    // 포괄손익계산서는 "손익계산서"를 포함하므로 더 긴 키워드부터 검사
    const STATEMENT_KEYWORDS = [
      "포괄손익계산서",
      "재무상태표",
      "손익계산서",
      "현금흐름표",
      "자본변동표",
    ] as const;

    const withReportBasePrefix = (title: string) => {
      const normalized = title.replace(/\s+/g, "");
      const matched = STATEMENT_KEYWORDS.find((s) => normalized.includes(s));
      if (!matched) return title;
      const prefix =
        comparisonSummary?.dsdMetadata?.reportBase === "consolidated"
          ? "연결 "
          : "";
      return `${prefix}${matched}`;
    };

    for (const chunk of chunks) {
      byId.set(chunk.id, chunk);
      if (seenTitles.has(chunk.title)) continue;
      seenTitles.add(chunk.title);
      const isNote = chunk.id.startsWith("note-");
      (isNote ? note : body).push({
        id: chunk.id,
        label: isNote ? chunk.title : withReportBasePrefix(chunk.title),
        hasChanges: false,
      });
    }

    const categories: CompareSidebarCategory[] = [];
    if (body.length > 0) categories.push({ label: "본문", sections: body });
    if (note.length > 0) categories.push({ label: "주석", sections: note });
    return { sidebarCategories: categories, chunkById: byId };
  }, [comparisonSummary]);

  const handleSidebarSectionClick = useCallback(
    (section: CompareSidebarSection) => {
      const chunk = chunkById.get(section.id);
      if (chunk) handleChunkClick(chunk);
    },
    [chunkById, handleChunkClick],
  );

  // 사이드바 보고서 정보 칩: comparisonSummary.dsdMetadata에서 파생
  const sidebarPeriodInfo = useMemo(() => {
    const metadata = comparisonSummary?.dsdMetadata;
    if (!metadata) return undefined;
    return {
      year: String(metadata.year),
      period: metadata.quarter,
      reportType: metadata.reportBase,
    };
  }, [comparisonSummary]);

  const handleHistorySelect = (selectedComparisonId: string) => {
    // 과거 비교를 새 탭에서 연다. 업로드 화면(현재 탭)은 그대로 유지.
    // chunkId 미지정 → 새 탭에서 summary 로드 시 첫 chunk로 자동 복구된다.
    openComparisonInNewTab(selectedComparisonId);
  };

  // ── HTML 다운로드 ──────────────────────────────────────────────────────────
  // [PoC] clone + vanilla 재구현 대신, 라이브와 같은 React 뷰어 번들을 임베드 데이터로
  // CSR 하는 self-contained HTML 을 만든다. 라이브 뷰는 lazy fetch 라 캐시에 일부만
  // 있으므로, 먼저 모든 chunk 상세를 fetchQuery 로 확보한 뒤 섹션 데이터를 직렬화한다.
  const { exportNow: handleDownloadHtml, isExporting: isExportingHtml } =
    useHtmlExport({
      // 통계 집계용 — 실패해도 다운로드엔 영향 없게 fire-and-forget.
      onLogged: () => logIxdDsdCompareDownload().catch(() => {}),
      onError: () => toast.error("HTML 다운로드에 실패했습니다."),
      build: async () => {
        if (!comparisonSummary?.chunks.length || !comparisonId) {
          toast.warning("다운로드할 비교 결과가 없습니다.");
          return null;
        }
        const chunks = comparisonSummary.chunks;
        const details = await Promise.all(
          chunks.map((chunk) =>
            queryClient.fetchQuery({
              ...queries.comparison(comparisonId)._ctx.chunk(chunk.id),
              staleTime: 10 * 60 * 1000,
              gcTime: 30 * 60 * 1000,
            }),
          ),
        );
        // 오프라인 파일에서 경로가 깨지지 않도록 사이드바 로고를 data URI 로 임베드.
        const logoDataUri = await fetchAssetAsDataUri(
          "/login-assets/invector-logo.svg",
        );

        // 헤더 태그 한글 라벨: IXD editor preview 의 th 헤더 텍스트로 element_id → 한글명
        // 맵을 만든다(라이브 compare-content-view 동일). 라벨은 부가정보라 실패해도 export 진행.
        let ixdLabelEntries: [string, string][] | undefined;
        // 연동(same-value) 인덱스는 라이브와 같은 입력(현재 fsType 보고서 전체)으로
        // 여기서 만들어 싣는다 — 근거는 export/linkage-index.ts.
        let linkageIndex: SerializedLinkageIndex | undefined;
        const ixdReportId = comparisonSummary.ixdReportId;
        if (ixdReportId) {
          // fetch / 라벨 / 연동 인덱스를 각각 감싼다. 라벨은 실패해도 진행하는 부가정보
          // 지만 연동 인덱스는 다운로드본의 핵심 동작(연동 표시·이동)을 좌우하므로,
          // 한 try 에 묶으면 라벨 조립의 예외가 인덱스까지 같이 날린다.
          const editorPreview = await queryClient
            .fetchQuery({
              ...queries.ixdEditor(ixdReportId)._ctx.preview(),
              staleTime: 10 * 60 * 1000,
              gcTime: 30 * 60 * 1000,
            })
            .catch((error) => {
              // 조용히 폴백하면 "다운로드본에서만 연동이 안 보인다" 제보의 원인을
              // 추적할 방법이 없다.
              Sentry.captureException(error);
              return undefined;
            });

          try {
            const labelMap = new Map<string, string>();
            for (const chunk of [
              ...(editorPreview?.consolidatedChunks ?? []),
              ...(editorPreview?.separateChunks ?? []),
            ]) {
              for (const dlView of chunk.dlViews) {
                if (!dlView.table) continue;
                for (const rows of Object.values(dlView.table)) {
                  for (const [id, label] of buildTableLabelMap(rows)) {
                    if (!labelMap.has(id)) labelMap.set(id, label);
                  }
                }
              }
            }
            ixdLabelEntries = Array.from(labelMap);
          } catch (error) {
            // 라벨 실패 시 헤더 태그는 raw id 로 표시(태그 자체는 동작).
            Sentry.captureException(error);
          }

          try {
            if (editorPreview) {
              linkageIndex = serializeLinkageIndex(
                buildCellIdentityIndex(
                  (selectedFsType === "consolidated"
                    ? editorPreview.consolidatedChunks
                    : editorPreview.separateChunks) ?? [],
                ),
              );
            }
          } catch (error) {
            // 연동 인덱스는 뷰어가 섹션 청크로 만든다(라이브보다 좁아진다).
            Sentry.captureException(error);
          }

          // 연동 인덱스가 빠지면 다운로드본은 "성공" 한 채로 연동 표시·이동이 통째로
          // 사라진다 — 사용자가 파일을 열어보고 나서야 아는 조용한 실패다. fetch 실패와
          // 조립 실패 둘 다 여기로 모이므로 경고는 한 곳에서 한 번만 낸다.
          // (라벨 실패는 헤더 태그가 raw id 로 degrade 될 뿐이라 알리지 않는다.)
          if (!linkageIndex) {
            toast.warning(
              "연동 정보를 불러오지 못해 다운로드본에 연동 표시가 빠집니다.",
            );
          }
        }
        // 라이브 IXD 패널과 동일한 청크 셋(매핑된 detail.ixdChunks)을 그대로 쓴다.
        // (구 buildSectionIxdChunks 는 매칭이 매핑 밖 청크를 가리킬 때 그 청크까지 합쳐
        //  넣어 앱보다 IXD 청크가 더 보이게 했다 — 앱과 불일치하여 제거.)
        const sections = chunks.map((chunk, index) => ({
          id: chunk.id,
          title: chunk.title,
          dsdSubChunks: details[index]?.dsdChunk?.subChunks ?? [],
          ixdChunks: details[index]?.ixdChunks ?? [],
          dsdHeaderTags: details[index]?.dsdHeaderTags,
          // 클릭 포커싱의 DL·기간 제약이 이 영역을 쓴다. 빠지면 다운로드본만 제약 없이
          // 다른 DL 의 동명 셀로 이동한다.
          dlRegions: buildDsdDlRegionViews(
            details[index]?.dsdDlRegions,
            details[index]?.ixdChunks,
          ),
          periodRegions: details[index]?.dsdPeriodRegions ?? null,
          lineItemRegions: buildDsdLineItemRegionViews(
            details[index]?.dsdLineItemRegions,
            details[index]?.dsdDlRegions,
            details[index]?.ixdChunks,
          ),
          memberRegions: buildDsdMemberRegionViews(
            details[index]?.dsdMemberRegions,
            details[index]?.dsdDlRegions,
            details[index]?.ixdChunks,
          ),
        }));
        const baseName = comparisonSummary.companyName?.trim() || "DSD_IXD";
        // 뷰어 번들(React + 결과 뷰 트리 IIFE 문자열)은 무겁고 다운로드 시점에만
        // 필요하므로, 정적 import 대신 동적 import 로 별도 청크로 분리한다.
        const { buildIxdDsdViewerHtml } = await import(
          "@/components/ixd-dsd-compare/export/build-viewer-html"
        );
        const html = buildIxdDsdViewerHtml(
          {
            sections,
            categories: sidebarCategories,
            periodInfo: sidebarPeriodInfo,
            dsdFilename:
              dsdFile?.name ?? comparisonSummary.dsdFilename ?? undefined,
            ixdFilename:
              ixdFile?.name ?? comparisonSummary.ixdFilename ?? undefined,
            logoDataUri: logoDataUri ?? undefined,
            ixdLabelEntries,
            linkageIndex,
          },
          "DSD - IXD 비교",
        );
        return { html, filename: `${baseName}_비교.html` };
      },
    });

  if (!isCompareView) {
    return (
      <div className="flex h-screen w-full">
        <LeftSidebar activeMenu="ixd-compare" />
        <InitialUploadView
          sourceMode={sourceMode}
          onSourceModeChange={handleSourceModeChange}
          onDsdSelect={handleDsdSelect}
          onIxdSelect={handleIxdSelect}
          selectedIxdReportId={selectedIxdReportId}
          onIxdReportSelect={setSelectedIxdReportId}
          dsdLoading={false}
          ixdLoading={false}
          dsdFile={dsdFile}
          ixdFile={ixdFile}
          onCompare={handleCompare}
          canCompare={!!canCompare}
          isComparing={isComparing}
          onHistorySelect={handleHistorySelect}
          onFuzz={handleFuzz}
          isFuzzing={fuzzMutation.isPending}
        />
      </div>
    );
  }

  return (
    <>
      <SidebarProvider
        className="h-full w-full"
        style={
          {
            "--sidebar-width": "192px",
            "--sidebar-width-icon": "3rem",
            minHeight: 0,
          } as React.CSSProperties
        }
      >
        <CompareSidebar
          className="bg-background-gray-normal"
          periodInfo={sidebarPeriodInfo}
          categories={sidebarCategories}
          activeSection={activeChunkId}
          onSectionClick={handleSidebarSectionClick}
        />
        <SidebarInset className="flex flex-col min-w-0 bg-background-gray-normal">
          <CompareContentView
            ixdRendererRef={ixdRendererRef}
            dsdFile={dsdFile}
            ixdFile={ixdFile}
            currentIxdChunks={currentIxdChunks}
            activeIxdChunk={activeIxdChunk}
            activeChunk={activeChunk}
            dsdPreview={dsdPreview}
            headerTagsByDsdId={headerTagsByDsdId}
            dlRegions={dlRegions}
            periodRegions={periodRegions}
            lineItemRegions={lineItemRegions}
            memberRegions={memberRegions}
            isSyncScroll={isSyncScroll}
            navigation={navigation}
            matchNavigation={matchNavigation}
            focusedDsdResetKey={focusedDsdResetKey}
            onCompareFocusClose={handleCompareFocusClose}
            isChunkLoading={isChunkLoading}
            isChunkFetching={isChunkFetching}
            onFocusIdentityLocation={handleFocusIdentityLocation}
            onDownloadHtml={handleDownloadHtml}
            isDownloadingHtml={isExportingHtml}
          />
        </SidebarInset>
      </SidebarProvider>
    </>
  );
};
